import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AssessmentDto } from '../shared/model/assessment-dto';
import { ScheduleDrive } from '../shared/model/hr-dto';
import { ApiResponse } from '../shared/model/response-format';
import {AppClient} from "../../environments/client";
import { environment } from 'src/environments/environment';
import { hasCookieSession } from '../shared/utils/auth-session';
import { Perm, PermissionKey, PermissionService } from '../shared/service/permission.service';

/**
 * The permission each dashboard tab needs, keyed by the tab key shared between
 * HrSidebarComponent.tabs and DriveDetailsComponent.toggleButton(). Tabs absent from here are
 * open to everyone.
 *
 * Only the tab's own name opens it — holding an action that lives inside a tab (e.g.
 * `Reset Candidate Password`) no longer shows the tab, and there is no umbrella name — each
 * tab is granted on its own. Order matters: it is the order the first-allowed-tab fallback walks,
 * and matches the sidebar (SR first).
 */
export const HR_TAB_PERMISSIONS: Record<string, PermissionKey> = {
  button6: Perm.SR_TAB,
  button1: Perm.DRIVES_TAB,
  button2: Perm.CANDIDATES_TAB,
  button3: Perm.CONSOLIDATED_REPORT_TAB,
  button4: Perm.ASSESSMENT_TAB,
  button5: Perm.STATS_TAB,
};

/** tab the HR dashboard lands on when nothing is persisted yet ('button6' = SR) */
export const HR_DEFAULT_TAB = 'button6';

@Injectable({
  providedIn: 'root',
})
export class HrService {

  /** True when the user holds the tab's own permission. */
  canViewTab(tabKey: string): boolean {
    const required = HR_TAB_PERMISSIONS[tabKey];
    return !required || this._permissionServices.can(required);
  }

  /** First tab the user may open, or null when they hold no tab permission at all. */
  firstVisibleTab(): string | null {
    return Object.keys(HR_TAB_PERMISSIONS).find((key) => this.canViewTab(key)) ?? null;
  }

  deployedClient = environment.deployedClient;

  baseUrl = environment.baseUrl;
  videoMerge = environment.videoMergeFeed
  apiUrls = {
    addScheduleDrive: "/api/v1/drive-config/schedule",
    getAssessmentDetails: "/api/v1/assessment-config/get-all-assessment",
    getDriveDetails: "/api/v1/drive-config/get-all",
    getDriveById: "/api/v1/drive-config/get-by-id/",
    getMailTemplates:'/api/v1/user/get-mail-templates/',
    getAllUsersOfDrive: "/api/v1/drive-config/get-all-users-of-drive",
    getAllInvitedUsersOfDrive: "/api/v1/drive-config/get-workday-drive-users-data",
    getAllInvitedUsersOfSrDrive: "/api/v1/drive-config/get-workday-drive-users-data-sr",
    candidateViewDetails: "/api/v1/drive-config/get-assessment-score",
    downloadReports: "/api/v1/reports/user/download-data",
    getCandidateProfileById: "/api/v1/user/get-candidate-profile-details/",
    downloadReportsForAllCandidates: "/api/v1/reports/drive-config/download-users-of-drive-in-excel/",
    generateReportsForDownload: "/api/v1/drive-config/generate-users-of-drive-in-excel/",
    mergeChunksInCandidate: "/api/v1/merge/merge-chunk-files-for-drive",
    unMergeChunkFiles: "/api/v1/file/get-user-feeds/",
    extendScheduledDrive: "/api/v1/drive-config/extend-and-end-drive",
    getAllPrimaryDrives: "/api/v1/drive-config/get-all-primary-drives",
    downloadMergedVideo : "/api/v1/reports/drive-config/download-merged-video-details",
    downloadMultipleDrive: "/api/v1/reports/drive-config/download-multiple-drives-report-in-excel",
    getShortlistDrive: "/api/v1/drive-config/get-all-to-schedule-new-drive",
    blackListCandidate: "/api/v1/user/block-candidate/",
    getAllCandidates: "/api/v1/drive-config/get-all-candidates",
    discoverIdentity: "/api/v1/user/discover-user-data/identity",
    discoverAccountTraces: "/api/v1/user/discover-user-data/account-traces",
    discoverDriveEnrollments: "/api/v1/user/discover-user-data/drive-enrollments",
    discoverDriveDetail: "/api/v1/user/discover-user-data/drive-detail/",
    mergeAllCandidateFeeds: "/api/v1/merge/merge-candidate-video-by-drive/",
    downloadProctoringReport : "/api/v1/reports/user/download-proctoring-details/",
    sendMailToUser: "/api/v1/drive-config/send-mail-drive-users",
    shortListByDrive: "/api/v1/short-listed/shortlist-candidate-by-drive",
    getShortListedData: "/api/v1/short-listed/get-shortlisted-candidates",
    uploadCSVFile: "/api/v1/drive-config/summary-csv-file",
    AIProctoringResult: "/api/v1/drive-config/get-candidate-drive-report/",
    mergeShortlistedFeed: "/api/v1/reports/drive-config/download-merged-video-details-for-shortlisted-candidate/",
    shortListCandidates: "/api/v1/drive-config/save-shortlist-candidate",
    downloadAllVideoResponse: "/api/v1/reports/drive-config/download-candidate-video-folder-details/",
    downloadShortlistedVideoResponse: "/api/v1/reports/drive-config/download-candidate-video-folder-details-for-shortlisted-candidate/",
    mergeShortlistedCandidate: "/api/v1/merge/merge-shortlisted-candidate-chunks-by-drive/",
    downloadReportForShortlisted: "/api/v1/reports/drive-config/download-shortlisted-candidate-report-in-excel/",
    downloadAllReports: "/api/v1/reports/drive-config/download-all-reports-of-drive-in-excel/",
    saveShortlistType: "/api/v1/short-listed/save-shortlist-type-candidate",
    addCandidateToShortlist:"/api/v1/drive-config/add-candidate-to-shortlist",
    removeCandidateFromShortlist:"/api/v1/drive-config/Remove-candidate-from-shortlist",
    downloadQuestionExplanationReport: "/api/v1/reports/drive-config/download-candidate-pop-up-question-details/",
    getAllGeneratedReports: "/api/v1/drive-config/find-all-reports-generated-of-drive-in-excel/",
    downloadExcelReports: "/api/v1/reports/drive-config/download-excel-by-file-id/",
    downloadAllCandidateBotAssessmentReport:'/api/v1/reports/inter-bot-report/download-interview-report-by-fileName/',
    allAssessmentReport: "/api/v1/reports/drive-config/generate-users-of-drive-in-excel/",
    allCandidateBotAssessmentReport:'/api/v1/reports/inter-bot-report/generate-interview-report-excel-by-drive/',
    shortlistedAssessmentReport: "/api/v1/reports/drive-config/generate-shortlist-users-of-drive-in-excel/",
    allCandidateMergedUrl: "/api/v1/reports/drive-config/generate-all-users-of-drive-merged-urls-in-excel/",
    generateShortlistedMergedUrl: "/api/v1/reports/drive-config/generate-shortlist-users-of-drive-merged-urls-in-excel/",
    generateAllCandidateAssessmentSummaryReport: "/api/v1/reports/drive-config/generate-summary-all-users-of-drive-in-excel/",
    generateAllUsersBasicDetailsWithResumeReport: "/api/v1/reports/drive-config/generate-all-users-basic-details-with-resume-in-excel/",
    shortlistCandidateBasicDetailsWithResume: "/api/v1/reports/drive-config/generate-shortlist-users-basic-details-with-resume-in-excel/",
    generateShortlistedCandidateAssessmentSummaryReport: "/api/v1/reports/drive-config/generate-summary-shortlist-users-of-drive-in-excel/",
    generateNonAttemptedCandidateAssessmentReport :"/api/v1/reports/drive-config/generate-not-attempted-users-of-drive-in-excel/",
    allInterviewResponse: "/api/v1/reports/drive-config/generate-all-users-of-drive-interview-responses-in-excel/",
    shortlistedInterviewResponse: "/api/v1/reports/drive-config/generate-shortlist-users-of-drive-interview-responses-in-excel/",
    regiteredCandidateAssessmentReportResponse:"/api/v1/reports/drive-config/generate-registered-users-report/",
    QuestionExplanationReport: "/api/v1/reports/drive-config/generate-all-users-of-drive-pop-question-responses-in-excel/",
    detectedMonitorReport:"/api/v1/candidate-app-monitoring/get/",
    candidateAudioProcess: "/api/v1/assessment/candidate-audio-process",
    candidateProctoringProcess: "/api/v1/assessment/process-candidate-proctoring-result",
    candidateCodingResult: "/api/v1/assessment/candidate-coding-result",
    getPupularAssesment: '/api/v1/drive-config/get-popular-assessments',
    getcountActiveDrive: '/api/v1/drive-config/get-count-of-active-drives',
    experienceCandidateChart:
      '/api/v1/drive-config/get-candidate-experience-details',
    getAssessmentDetailDashboard:
      '/api/v1/drive-config/get-all-assessments-for-dashboard',
    saveDriveJD: '/api/v1/drive-config/save-drive-jd',
    chatBtwnBotAndCandidate: '/api/v1/assessment/get-candidate-interview-response-by-callId/',
    getInterviewBotScore :'/api/v1/candidate-audio-score/get-interview-bot-score/',
    getInterviewTimestamps:'/api/v1/assessment/get-interview-timestamps/',
    getProctoringImages :'/api/v1/assessment/get-proctoring-images/',
    sendCandidateEmail: '/api/v1/drive-config/send-mail-to-candidate/',
    twoWayReportGenerate :'/api/v1/reports/inter-bot-report/generate-interview-report-excel/',
    twoWayReportDownload: '/api/v1/reports/inter-bot-report/download-interview-report-by-callId/',
    interBotReportDownloadBlob: '/api/v1/reports/inter-bot-report/download-blob?driveId=',
    exportQuestionsExcel: '/api/v1/drive-config/export-questions-excel',
    resendReportsWorkdayForNoninterviewBot: '/api/v1/drive-config/send-candidate-reports-to-workday',
    resendReportsWorkdayInterviewBot: '/api/v1/reports/candidate-audio-score/trigger-interview-report-to-workday-by-drive',
    reEvaluateInterviewBot: '/api/v1/assessment/run-interview-bot-evaluation-by-callId/',
    resendInviteForWorkday: '/api/v1/drive-config/send-mail-to-pending-candidates',
    viewDrivesOfCandidateById: '/api/v1/drive-config/get-drives-of-candidate/',
    workDayConsolidatedErrors: '/api/v1/drive-config/workday-missing-report-users',
    resetPasswordForCandidate : '/api/v1/user/reset-candidate-password/',
    unblockCandidate :'/api/v1/user/unblock-candidate/',
    generateReportForWodayUsers:'/api/v1/reports/drive-config/download-invite-users-excel/',
    updateDriveStatus:(driveId:string)=> `/api/v1/assessment/calculate-assessment-score/${driveId}`,
    setDriveSettings:(driveId:string)=>`/api/v1/drive-config/update-candidate-drive-setting/${driveId}`,
    saveRetriggerPipeline:(driveId:string)=>`/api/v1/drive-config/process-drive-jd-manually/${driveId}`,
    getAllActiveScheduleDrive : '/api/v1/drive-config/get-all-active-drives-for-schedule',
    resetLastAttemptOfCandidate:'/api/v1/drive-config/reset-exam-attempt',
    deRegisterCandidateFromDrive:'/api/v1/drive-config/unregister-user',
    generateConsolidatedReport : '/api/v1/reports/drive-config/generate-consolidated-report',
    getAllConsolidatedReportPagination:"/api/v1/drive-config/find-all-consolidated-reports",
    getAllSRDrives: '/api/v1/sourcing-requisitions/get-all-sr-drives',
    getAllReattemptsListForCandidate:"/api/v1/drive-config/get-past-attempts/",
    getTemplates: '/api/v1/user/web/get-template-names',
    resumeUrlForWorkdayUsers  : '/api/v1/file/signed/url/s3/',
    searchProctoringProcesses: '/api/v1/merge/proctoring-processes/search',
    retriggerProctoringProcess: '/api/v1/merge/retrigger-proctoring',
    retriggerProctoringAllOrSingle: '/api/v1/merge/retrigger-proctoring-all-or-single',
    updateProctoringField: '/api/v1/drive-config/update-proctoring-field',
    retriggerMergeAllOrSingle: '/api/v1/merge/retrigger-merge-all-or-single',
    retriggerMergeSingle: '/api/v1/merge/retrigger-merge',
    mergeRequestStatus: '/api/v1/drive-config/merge-request-status',
    importShortlistedUsersViaDriveId :'/api/v1/drive-config/import-new-shortlisted-candidates',
    getAssessmentById: '/api/v1/assessment-config/get-assessment-by-id/',
    generatePublicCandidateCode: '/api/v1/candidate-share/generate',
    getWorkdayErrorlogForWorkdayUser : '/api/v1/drive-config/get-workday-report/',
    bulkSaveShortlistCandidate: '/api/v1/short-listed/bulk-save-shortlist-candidate',
    toggleDriveFavourite: '/api/v1/drive-config/toggle-drive-favourite',
    getAssessmentByAssessmentId: "/api/v1/assessment-config/assessments/",
    audioEvaluationTrackingReport: '/api/v1/audio/evaluation-tracking/report',
    retryAudioEvaluationEvent: '/api/v1/audio/evaluation-tracking/',
    retryFailedAudioEvents: '/api/v1/audio/evaluation-tracking/retry-failed-events/',
    codingEvaluationTrackingReport: '/api/v1/coding/evaluation-tracking/report',
    retryCodingEvaluationEvent: '/api/v1/coding/evaluation-tracking/',
    retryNonSuccessCodingEvents: '/api/v1/coding/evaluation-tracking/retry-non-success-events/',
    bigqueryEvaluationTrackingReport: '/api/v1/bigquery/evaluation-tracking/report',
    retryBigqueryEvaluationEvent: '/api/v1/bigquery/evaluation-tracking/',
    retryNonSuccessBigqueryEvents: '/api/v1/bigquery/evaluation-tracking/retry-non-success-events/',
    caseStudyEvaluationTrackingReport: '/api/v1/case-study/evaluation-tracking/report',
    retryCaseStudyEvaluationEvent: '/api/v1/case-study/evaluation-tracking/',
    retryNonSuccessCaseStudyEvents: '/api/v1/case-study/evaluation-tracking/retry-non-success-events/',
    ideEvaluationTrackingReport: '/api/v1/ide/evaluation-tracking/report',
    retryIdeEvaluationEvent: '/api/v1/ide/evaluation-tracking/',
    retryNonSuccessIdeEvents: '/api/v1/ide/evaluation-tracking/retry-non-success-events/',
    jupyterEvaluationTrackingReport: '/api/v1/jupyter/evaluation-tracking/report',
    retryJupyterEvaluationEvent: '/api/v1/jupyter/evaluation-tracking/',
    retryNonSuccessJupyterEvents: '/api/v1/jupyter/evaluation-tracking/retry-non-success-events/',
    botEvaluationTrackingReport: '/api/v1/bot/evaluation-tracking/report',
    retryBotEvaluationEvent: '/api/v1/bot/evaluation-tracking/',
    retryFailedBotEvents: '/api/v1/bot/evaluation-tracking/retry-failed-events/',
    getPracticeBotSummary: '/api/v1/practice-bot/get-practice-bot-summary-evaluation/',
    runPracticeBotSummaryEvaluation: '/api/v1/practice-bot/run-practice-bot-summary-evaluation/',
    codeCompileTrackingReport: '/api/v1/code-compile-tracking/report',
    retryCodeCompileEvent: '/api/v1/code-compile-tracking/',
    retryNonSuccessCodeCompileEvents: '/api/v1/code-compile-tracking/retry-failed/',
    getIqigaiClients: '/api/v1/tenant/clients/all',
  }
  /**
   * Currently selected HR dashboard tab ('button1'..'button6').
   * Written by the HR sidebar, consumed by DriveDetailsComponent.
   * Falls back to HR_DEFAULT_TAB (SR) on a first visit.
   */
  activeHrTab$ = new BehaviorSubject<string>(
    localStorage.getItem('selectedToggleButton') || HR_DEFAULT_TAB
  );

  /** Root service outlives logout (no reload) but localStorage doesn't — re-read so a fresh login lands on Drives. */
  syncActiveHrTab(): void {
    const tab = localStorage.getItem('selectedToggleButton') || HR_DEFAULT_TAB;
    if (this.activeHrTab$.value !== tab) this.activeHrTab$.next(tab);
  }

  constructor(
    private _http: HttpClient,
    private readonly _permissionServices: PermissionService,
  ) { }


  getIqigaiClients(payload: { pageNumber: number; pageSize: number }): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getIqigaiClients
    );
  }

  addScheduleDrives(requestPayload: ScheduleDrive): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.addScheduleDrive,
      requestPayload
    );
  }
  getAssessmentDetail(requestPayload): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getAssessmentDetails,
      requestPayload
    );
  }

  getDriveDetailsById(requestPayload): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getDriveById + requestPayload
    );
  }
  
  getResumeUrlForWorkdayUsers(id:string): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.resumeUrlForWorkdayUsers + id
    );
  }

  getMailTemplates(requestPayload): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getMailTemplates + requestPayload
    );
  }

  getDriveDetail(requestPayload): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getDriveDetails,
      requestPayload
    );
  }

  getAllUsersFordrive(requestPayload): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getAllUsersOfDrive,
      requestPayload
    );
  }

  getAllInvitedUsersFordrive(requestPayload): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getAllInvitedUsersOfDrive,
      requestPayload
    );
  }

  getAllInvitedUsersForSrDrive(requestPayload): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getAllInvitedUsersOfSrDrive,
      requestPayload
    );
  }


  getAllConsolidatedErrorsForWorkday(requestPayload): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.workDayConsolidatedErrors,
      requestPayload
    );
  }

  setAssessmentDriveSetting(requestPayload,driveId:string):Observable<ApiResponse<any>>{
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.setDriveSettings(driveId),
      requestPayload
    );
  }
  saveRetriggerPipelineSetting(driveId:string):Observable<ApiResponse<any>>{
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.saveRetriggerPipeline(driveId)
    );
  }

  getSectionScore(driveAssessmentId): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.candidateViewDetails,
      driveAssessmentId
    );
  }

  downloadReportsForCandiadte(requestPayload): Observable<ApiResponse<any>> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.post<ApiResponse<any>>(
      url + this.apiUrls.downloadReports,
      requestPayload
    );
  }

   getProctoringImages(id,driveId): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getProctoringImages + id + '/' +driveId);
  }

  downloadReportForWorkdayUsers(driveId:string): Observable<ApiResponse<any>> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.generateReportForWodayUsers + driveId);
  }
  

  downloadReportsForAllCandiadte(requestPayload): Observable<ApiResponse<any>> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url +
        this.apiUrls.downloadReportsForAllCandidates +
        requestPayload
    );
  }

  generateReportsForAllCandiadte(requestPayload): Observable<ApiResponse<any>> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }

    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.generateReportsForDownload + requestPayload
    );
  }

  resetCandidatesPassword(candidateId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.resetPasswordForCandidate + candidateId , {}
    );
  }

  unblockCandidateById(candidateId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.unblockCandidate + candidateId,{}
    );
  }

  generateReportForTwoWayOnly(callId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.twoWayReportGenerate + callId
    );
  }


    downloadReportForTwoWayOnly(callId: any): Observable<any> {
      let url = this.baseUrl;
      if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
        url = this.videoMerge;
      }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.twoWayReportDownload + callId
    );
  }

  getCandidateProfile(candidateId): Observable<any> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getCandidateProfileById + candidateId
    );
  }

  resendReportsForWorkdayForNonInterviewBot(payload:{driveId:string,email?:[]}): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.resendReportsWorkdayForNoninterviewBot , payload
    );
  }

  resendReportsForWorkdayInterviewBot(payload:{driveId:string,email?:[]}): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.post<ApiResponse<any>>(
      url + this.apiUrls.resendReportsWorkdayInterviewBot , payload
    );
  }


  resendInviteForWorkday(payload:{driveId:string,email?:[]}): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.resendInviteForWorkday , payload
    );
  }


  viewCandidateDriveById(candidateId:string): Observable<any> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.viewDrivesOfCandidateById + candidateId
    );
  }

  reEvaluateInterviewBot(callId:string): Observable<any> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.reEvaluateInterviewBot + callId
    );
  }

  mergeChunksForEachCandiadte(requestPayload): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.post<ApiResponse<any>>(
      url + this.apiUrls.mergeChunksInCandidate,
      requestPayload
    );
  }

  extendScheduledDrive(requestPayload): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.extendScheduledDrive,
      requestPayload
    );
  }

  unMergedChunksFiles(
    appUserId,
    driveId,
    assessmentDriveSetId,
    chunkType
  ): Observable<any> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl +
        this.apiUrls.unMergeChunkFiles +
        appUserId +
        '/' +
        driveId +
        '/' +
        assessmentDriveSetId +
        '/' +
        chunkType
    );
  }

  getAllPrimaryDrives(requestPayload): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getAllPrimaryDrives,
      requestPayload
    );
  }

  downloadMergedVideo(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.downloadMergedVideo + '/' + driveId
    );
  }

  mergeAllCandidateFeeds(requestPayload): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.mergeAllCandidateFeeds + requestPayload
    );
  }
  downloadMultipleDriveReport(requestPayload): Observable<ApiResponse<any>> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.post<ApiResponse<any>>(
      url + this.apiUrls.downloadMultipleDrive,
      requestPayload
    );
  }

  getShortlistDrive(requestPayload) {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getShortlistDrive,
      requestPayload
    );
  }

  blackListCandidate(requestPayload) {
    return this._http.put<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.blackListCandidate + requestPayload,{}
    );
  }

  getAllCandidateList(requestPayload): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getAllCandidates,
      requestPayload
    );
  }

  downloadProctoringReport(requestPayload): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.downloadProctoringReport + requestPayload
    );
  }

  sendMailToUser(requestPayload): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.sendMailToUser,
      requestPayload
    );
  }

  shortListData(requestPayload): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.shortListCandidates,
      requestPayload
    );
  }

  shortListByDrive(requestPayload): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.shortListByDrive,
      requestPayload
    );
  }

  getShortListedData(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getShortListedData,
      requestPayload
    );
  }

  uploadCSVFile(requestPayload): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.uploadCSVFile,
      requestPayload
    );
  }

  AIProctoringResult(userAssessmentDriveSetId: any): Observable<any> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.AIProctoringResult + userAssessmentDriveSetId
    );
  }

  mergeShortlistedFeed(requestPayload): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.mergeShortlistedFeed + requestPayload
    );
  }

  downloadAllVideoResponse(driveId: any): Observable<any> {
    let url = this.baseUrl;
    url = this.videoMerge;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.downloadAllVideoResponse + driveId
    );
  }

  downloadShortlistedVideoResponse(driveId: any): Observable<any> {
    let url = this.baseUrl;
    url = this.videoMerge;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.downloadShortlistedVideoResponse + driveId
    );
  }

  mergeShortlistedCandidate(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.mergeShortlistedCandidate + driveId
    );
  }

  downloadReportForShortlisted(requestPayload): Observable<ApiResponse<any>> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.downloadReportForShortlisted + requestPayload
    );
  }

  downloadAllReports(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.downloadAllReports + driveId
    );
  }

  saveShortlistType(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.saveShortlistType,
      requestPayload
    );
  }

  addCandidateToShortlist(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.addCandidateToShortlist,
      requestPayload
    );
  }

  removeCandidateFromShortlist(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.removeCandidateFromShortlist,
      requestPayload
    );
  }

  downloadQuestionExplanationReport(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.downloadQuestionExplanationReport + driveId
    );
  }

  getAllGeneratedReports(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url+ this.apiUrls.getAllGeneratedReports + driveId
    );
  }

  downloadExcelReports(fileId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.downloadExcelReports + fileId
    );
  }
  downloadAllCandidateBotAssessmentReport(fileId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.downloadAllCandidateBotAssessmentReport + fileId
    );
  }

  allAssessmentReport(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.allAssessmentReport + driveId
    );
  }
  allCandidateBotAssessmentReport(driveId:any):Observable<any>{
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.allCandidateBotAssessmentReport + driveId
    );
  }

  downloadInterBotReportBlob(driveId: any): Observable<HttpResponse<Blob>> {
    let url = this.baseUrl;
    return this._http.get(
      url + this.apiUrls.interBotReportDownloadBlob + driveId,
      { responseType: 'blob', observe: 'response' }
    );
  }

  exportQuestionsExcel(payload: any): Observable<HttpResponse<Blob>> {
    return this._http.post(
      this.baseUrl + this.apiUrls.exportQuestionsExcel,
      payload,
      { responseType: 'blob', observe: 'response' }
    );
  }

  shortlistedAssessmentReport(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.shortlistedAssessmentReport + driveId
    );
  }

  allCandidateMergedUrl(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.allCandidateMergedUrl + driveId
    );
  }

  generateShortlistedMergedUrl(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url+ this.apiUrls.generateShortlistedMergedUrl + driveId
    );
  }

   generateAllCandidateAssessmentSummaryReport(driveId: any): Observable<any> {
     let url = this.baseUrl;
     if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
       url = this.videoMerge;
     }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.generateAllCandidateAssessmentSummaryReport + driveId
    );
  }

  generateAllUserBasicDetailsWithResumeReport(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.generateAllUsersBasicDetailsWithResumeReport + driveId
    );
  }

  shortlistCandidateBasicDetailsWithResume(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
   return this._http.get<ApiResponse<any>>(
     url + this.apiUrls.shortlistCandidateBasicDetailsWithResume + driveId
   );
 }
 
   generateShortlistedCandidateAssessmentSummaryReport(driveId: any): Observable<any> {
     let url = this.baseUrl;
     if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
       url = this.videoMerge;
     }
    return this._http.get<ApiResponse<any>>(
     url + this.apiUrls.generateShortlistedCandidateAssessmentSummaryReport + driveId
    );
  }
  generateNonAttemptedCandidateAssessmentReport(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.generateNonAttemptedCandidateAssessmentReport + driveId
    );
  }



  allInterviewResponse(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.allInterviewResponse + driveId
    );
  }

  shortlistedInterviewResponse(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.shortlistedInterviewResponse + driveId
    );
  }

  regiteredCandidateAssessmentReportResponse(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.regiteredCandidateAssessmentReportResponse + driveId
    );
  }
  popularAssestementData(): Observable<any> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getPupularAssesment
    );
  }
  getcountActiveDrive(): Observable<any> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getcountActiveDrive
    );
  }

  experienceCandidateChart(): Observable<any> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.experienceCandidateChart
    );
  }
  getAssessmentDetailDashboard(requestPayload): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getAssessmentDetailDashboard,
      requestPayload
    );
  }
  QuestionExplanationReport(driveId: any): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.get<ApiResponse<any>>(
      url + this.apiUrls.QuestionExplanationReport + driveId
    );
  }
  detectedMonitor(
    candidateId: any,
    userAssessmentDriveSetId: any
  ): Observable<any> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl +
        this.apiUrls.detectedMonitorReport +
        candidateId +
        '/' +
        userAssessmentDriveSetId
    );
  }

  candidateAudioProcess(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.candidateAudioProcess,
      requestPayload
    );
  }

      candidateCodingResult(requestPayload:any): Observable<any> {
        return this._http.post<ApiResponse<any>> (
          this.baseUrl + this.apiUrls.candidateCodingResult, requestPayload
        );

      }

      saveDriveJD(requestPayload:any): Observable<any> {
        return this._http.post<ApiResponse<any>> (
          this.baseUrl + this.apiUrls.saveDriveJD, requestPayload
        );

      }


  candidateProctoringProcess(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.candidateProctoringProcess,
      requestPayload
    );
  }

  updateDriveStatus(driveId: string) {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.updateDriveStatus(driveId),
      {}
    );
  }

   getChatBetweenBotAndCandidate(callId): Observable<any> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.chatBtwnBotAndCandidate + callId
    );
  }

    getInterviewBotScore(callId): Observable<any> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getInterviewBotScore + callId
    );
  }

    getInterviewTimestamps(callId): Observable<any> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getInterviewTimestamps + callId
    );
  }

  sendCandidateEmail(userAssessmentDriveSetId: any): Observable<any> {
    const url = this.baseUrl + this.apiUrls.sendCandidateEmail + userAssessmentDriveSetId;
    // State-changing: cookie sessions must POST (a cookie GET is rejected).
    return hasCookieSession()
      ? this._http.post<ApiResponse<any>>(url, null)
      : this._http.get<ApiResponse<any>>(url);
  }

  getAllActiveScheduleDrive(requestPayload): Observable<any> {
    return this._http.post<any>(
      this.baseUrl + this.apiUrls.getAllActiveScheduleDrive,
      requestPayload
    );
  }


  resetLastattemptOfCandidateFromDrive(requestPayload): Observable<any> {
    return this._http.patch<any>(
      this.baseUrl + this.apiUrls.resetLastAttemptOfCandidate,
      requestPayload ,{
        headers: { 'X-Skip-Error-Interceptor': 'true' }
      }
    );
  }
  
  deRegisterCandidateFromDrive(requestPayload): Observable<any> {
    return this._http.patch<any>(
      this.baseUrl + this.apiUrls.deRegisterCandidateFromDrive,
      requestPayload
    );
  }
  
  generateConsolidatedReport(reportsPayload:any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
     this.baseUrl + this.apiUrls.generateConsolidatedReport,
      reportsPayload
    );
  }

   getAllConsolidatedReports(requestPayload): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getAllConsolidatedReportPagination,
      requestPayload
    );
  }

  getAllSRDrives(payload: any): Observable<any> {
    return this._http.post<any>(this.baseUrl + this.apiUrls.getAllSRDrives, payload);
  }

  getAllReattempts(driveId:string,candidateId:string){
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getAllReattemptsListForCandidate + driveId +'/' + candidateId
    );
  }

   getTemplates(): Observable<any> {
    return this._http.get<any>(
      this.baseUrl + this.apiUrls.getTemplates);
  }

  searchProctoringProcesses(requestPayload): Observable<ApiResponse<any>> {
      let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.post<ApiResponse<any>>(
      url + this.apiUrls.searchProctoringProcesses,
      requestPayload
    );
  }

  retriggerProctoringProcess(processId: string): Observable<ApiResponse<any>> {
      let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.post<ApiResponse<any>>(
      url + this.apiUrls.retriggerProctoringProcess + '?processId=' + processId,
      {}
    );
  }

  // Retrigger proctoring all or single candidate
  retriggerProctoringAllOrSingle(requestPayload): Observable<ApiResponse<any>> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.post<ApiResponse<any>>(
      url + this.apiUrls.retriggerProctoringAllOrSingle,
      requestPayload
    );
  }

  // Updates a single proctoring field, the untouched fields are sent as null
  updateProctoringField(requestPayload): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.updateProctoringField,
      requestPayload
    );
  }

  getMergeRequestStatus(requestPayload): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.mergeRequestStatus,
      requestPayload
    );
  }

  // Retrigger merge all or single candidate
  retriggerMergeAllOrSingle(requestPayload): Observable<ApiResponse<any>> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.post<ApiResponse<any>>(
      url + this.apiUrls.retriggerMergeAllOrSingle,
      requestPayload
    );
  }

  // Retrigger merge for single candidate by driveId and userId
  retriggerMergeSingle(driveId: string, userId: string, force: boolean = false): Observable<ApiResponse<any>> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.post<ApiResponse<any>>(
      url + this.apiUrls.retriggerMergeSingle + `?driveId=${driveId}&userId=${userId}&force=${force}`,
      {}
    );
  }

  importShortlistedUsersViaDriveIdApi(requestPayload: {currentDriveId: any;driveIdsToImport: any[]}): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.importShortlistedUsersViaDriveId,
      requestPayload
    );
  }

  // Only for work day users
  getWorkdayErrorlogForCandidate(driveId:string,candidateId:string,userAssessmentDriveSetId:string): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getWorkdayErrorlogForWorkdayUser + driveId + '/' + candidateId + '/' + userAssessmentDriveSetId
    );
  }

  /**
   * Generate public candidate code for sharing candidate report
   * @param payload - Object containing drive_id, candidate_id, id, ib_id
   * @returns Observable with generated code
   */
  generatePublicCandidateCode(payload: any): Observable<any> {
    return this._http.post<any>(
      this.baseUrl + this.apiUrls.generatePublicCandidateCode,
      payload
    );
  }


  bulkSaveShortlistCandidate(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.bulkSaveShortlistCandidate,
      requestPayload
    );
  }

  toggleDriveFavourite(payload: { driveId: string }): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.toggleDriveFavourite,
      payload
    );
  }

  startProctoringForIndividualCandidate(requestPayload): Observable<any> {
    let url = this.baseUrl;
    if(this.deployedClient == AppClient.PROD_MAIN || this.deployedClient == AppClient.PROD_LITE){
      url = this.videoMerge;
    }
    return this._http.post<ApiResponse<any>>(
      url + this.apiUrls.retriggerProctoringAllOrSingle,
      requestPayload
    );
  }


  getAssessmentByIdFromService(assessmentId: string): Observable<any> {
    return this._http.get<any>(
      this.baseUrl + this.apiUrls.getAssessmentByAssessmentId + assessmentId
    );
  }

  // Audio Evaluation Tracking Methods
  getAudioEvaluationTrackingReport(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.audioEvaluationTrackingReport,
      requestPayload
    );
  }

  retryAudioEvaluationEvent(eventId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryAudioEvaluationEvent + eventId + '/bulk/retry',
      {}
    );
  }

  retryAllFailedAudioEvents(driveId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryFailedAudioEvents + driveId,
      {}
    );
  }

  // Coding Evaluation Tracking Methods
  getCodingEvaluationTrackingReport(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.codingEvaluationTrackingReport,
      requestPayload
    );
  }

  retryCodingEvaluationEvent(eventId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryCodingEvaluationEvent + eventId + '/retry',
      {}
    );
  }

  retryAllNonSuccessCodingEvents(driveId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryNonSuccessCodingEvents + driveId,
      {}
    );
  }

  // BigQuery Evaluation Tracking Methods
  getBigqueryEvaluationTrackingReport(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.bigqueryEvaluationTrackingReport,
      requestPayload
    );
  }

  retryBigqueryEvaluationEvent(eventId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryBigqueryEvaluationEvent + eventId + '/retry',
      {}
    );
  }

  retryAllNonSuccessBigqueryEvents(driveId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryNonSuccessBigqueryEvents + driveId,
      {}
    );
  }

  // Case Study Evaluation Tracking Methods
  getCaseStudyEvaluationTrackingReport(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.caseStudyEvaluationTrackingReport,
      requestPayload
    );
  }

  retryCaseStudyEvaluationEvent(eventId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryCaseStudyEvaluationEvent + eventId + '/retry',
      {}
    );
  }

  retryAllNonSuccessCaseStudyEvents(driveId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryNonSuccessCaseStudyEvents + driveId,
      {}
    );
  }

  // IDE Evaluation Tracking Methods
  getIdeEvaluationTrackingReport(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.ideEvaluationTrackingReport,
      requestPayload
    );
  }

  retryIdeEvaluationEvent(eventId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryIdeEvaluationEvent + eventId + '/retry',
      {}
    );
  }

  retryAllNonSuccessIdeEvents(driveId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryNonSuccessIdeEvents + driveId,
      {}
    );
  }

  // Jupyter Evaluation Tracking Methods
  getJupyterEvaluationTrackingReport(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.jupyterEvaluationTrackingReport,
      requestPayload
    );
  }

  retryJupyterEvaluationEvent(eventId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryJupyterEvaluationEvent + eventId + '/retry',
      {}
    );
  }

  retryAllNonSuccessJupyterEvents(driveId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryNonSuccessJupyterEvents + driveId,
      {}
    );
  }

  // Bot Evaluation Tracking Methods
  getBotEvaluationTrackingReport(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.botEvaluationTrackingReport,
      requestPayload
    );
  }

  retryBotEvaluationEvent(eventId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryBotEvaluationEvent + eventId + '/retry',
      {}
    );
  }

  retryAllFailedBotEvents(driveId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryFailedBotEvents + driveId,
      {}
    );
  }

       getPracticeBotSummary(driveId:string, candidateID:string){
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getPracticeBotSummary + driveId + '/' + candidateID
    );
  }

   runPracticeBotSummaryEvaluation(driveId:string, candidateID:string){
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.runPracticeBotSummaryEvaluation + driveId + '/' + candidateID
    );
  }

  // Code Compile Tracking Methods
  getCodeCompileTrackingReport(requestPayload: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.codeCompileTrackingReport,
      requestPayload
    );
  }

  retryCodeCompileEvent(eventId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryCodeCompileEvent + eventId + '/retry',
      {}
    );
  }

  retryAllNonSuccessCodeCompileEvents(driveId: string): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.retryNonSuccessCodeCompileEvents + driveId,
      {}
    );
  }

  /* ---- discover-user-data (candidate 360 view) ---- */
  getDiscoverIdentity(email: string): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.discoverIdentity + `?email=${encodeURIComponent(email)}`
    );
  }

  getDiscoverAccountTraces(email: string): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.discoverAccountTraces + `?email=${encodeURIComponent(email)}`
    );
  }

  getDiscoverDriveEnrollments(email: string): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.discoverDriveEnrollments + `?email=${encodeURIComponent(email)}`
    );
  }

  // section: overview | media | evaluations | activity
  getDiscoverDriveDetail(driveId: string, section: string, appUserId: string): Observable<ApiResponse<any>> {
    return this._http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.discoverDriveDetail + driveId + '/' + section + `?appUserId=${appUserId}`
    );
  }
}
